import DOMPurify from 'dompurify';

/**
 * Sanitize email HTML and remove cid: references (inline images)
 * @param html - The email HTML content
 * @returns Sanitized HTML string with cid: references removed
 */
export const sanitizeEmailHtml = (html: string): string => {
  if (!html) return '';

  // First, do a simple string replacement to replace cid: URLs with empty data URLs
  // This prevents the browser from even attempting to fetch them
  let cleanedHtml = html.replace(/src=["']cid:[^"']*["']/gi, 'src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"');
  cleanedHtml = cleanedHtml.replace(/url\(['"]?cid:[^'")]*['"]?\)/gi, 'url("")');

  // Parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanedHtml, 'text/html');

  // Find and remove all img tags with cid: src (inline images like logos/signatures) - backup cleanup
  const images = doc.querySelectorAll('img[src]');
  images.forEach((img) => {
    const src = img.getAttribute('src') || '';

    if (src.startsWith('cid:')) {
      // Remove inline images (typically logos/signatures)
      img.remove();
    } else if (/^(data:|blob:)/i.test(src)) {
      // Allow data: and blob: URLs
      return;
    } else if (/^https?:\/\//i.test(src)) {
      // For http(s) URLs, validate they have a proper hostname
      try {
        const url = new URL(src);
        // Check if hostname is valid (not empty, not just whitespace, contains at least one dot or is localhost)
        if (!url.hostname || url.hostname.trim() === '' ||
            !(url.hostname.includes('.') || url.hostname === 'localhost')) {
          img.remove();
        }
      } catch (e) {
        // Invalid URL, remove it
        img.remove();
      }
    } else {
      // Remove images with unknown/invalid schemes
      img.remove();
    }
  });

  // Remove other elements that might reference cid: URLs
  const objects = doc.querySelectorAll('object[data], embed[src], source[src]');
  objects.forEach((el) => {
    const dataAttr = el.getAttribute('data') || el.getAttribute('src') || '';
    if (dataAttr.startsWith('cid:') || !/^(https?:|data:|blob:)/i.test(dataAttr)) {
      el.remove();
    }
  });

  // Also scrub inline CSS background-image: url(cid:...)
  const elementsWithStyle = doc.querySelectorAll<HTMLElement>('[style]');
  elementsWithStyle.forEach((el) => {
    const style = el.getAttribute('style') || '';
    if (/url\((['"]?)(cid:|ftp:|file:|chrome:)/i.test(style)) {
      el.style.backgroundImage = ''; // strip unsafe URLs
    }
  });

  // Sanitize with DOMPurify
  const sanitized = DOMPurify.sanitize(doc.documentElement.innerHTML, {
    ADD_ATTR: ['style'], // Allow inline styles
    ALLOWED_URI_REGEXP: /^(?:(?:https?|data|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i // Allow http/https/data/blob
  });

  return sanitized;
};
